import { Pencil } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatCurrency } from '../lib/utils'
import {
  type Equipment,
  EquipmentStatus,
  priceEstimateLabel,
  type VehicleClass,
  VehicleClassLabels,
  type WeightClass,
  WeightClassLabels,
} from '../models/equipment'
import { DetailRow, DiffRow, PendingBanner, type PendingKind } from './PendingDetailRows'

function fmtClass(value: VehicleClass | null): string {
  return value ? (VehicleClassLabels[value] ?? value) : ''
}

function fmtWeight(value: WeightClass | null): string {
  return value ? (WeightClassLabels[value] ?? value) : ''
}

function fmtPrice(value: number | null, datePriceEstimated?: string | null): string {
  if (value == null) return ''
  const estimate = priceEstimateLabel(datePriceEstimated)
  return estimate ? `${formatCurrency(value)} (${estimate})` : formatCurrency(value)
}

function fmtYear(value: number | null | undefined): string {
  return value != null ? String(value) : ''
}

export function EquipmentDetailsDialog({
  item,
  label,
  open,
  onOpenChange,
  onEdit,
}: {
  item: Equipment
  label: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Shows an Edit button; omit it for a row that can't be edited. */
  onEdit?: () => void
}) {
  const pending = item.pendingChange
  const kind: PendingKind | null =
    item.status === EquipmentStatus.PENDING_ADD
      ? 'add'
      : pending?.status === EquipmentStatus.PENDING_REMOVE
        ? 'remove'
        : pending
          ? 'change'
          : null
  // Only a PENDING_CHANGE carries new field values worth diffing against the live row.
  const change = kind === 'change' ? pending : null

  const title = [item.year, item.make, item.model].filter(Boolean).join(' ') || item.vin || label

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {kind && <PendingBanner kind={kind} />}
        <div className="grid grid-cols-2 gap-4">
          <DetailRow label="Vehicle number" value={item.vehicleNumber} />
          <DiffRow label="VIN" oldValue={item.vin} newValue={change?.vin} />
          <DiffRow label="Make" oldValue={item.make} newValue={change?.make} />
          <DiffRow label="Model" oldValue={item.model} newValue={change?.model} />
          <DiffRow
            label="Year"
            oldValue={fmtYear(item.year)}
            newValue={change ? fmtYear(change.year) : undefined}
          />
          <DiffRow
            label="Vehicle class"
            oldValue={fmtClass(item.vehicleClass)}
            newValue={change ? fmtClass(change.vehicleClass) : undefined}
          />
          <DiffRow
            label="Weight class"
            oldValue={fmtWeight(item.weightClass)}
            newValue={change ? fmtWeight(change.weightClass) : undefined}
          />
          <DiffRow label="License plate" oldValue={item.licensePlate} newValue={change?.licensePlate} />
          <DiffRow label="License state" oldValue={item.licenseState} newValue={change?.licenseState} />
          <DiffRow
            label="Stated value"
            oldValue={fmtPrice(item.price, item.datePriceEstimated)}
            newValue={change ? fmtPrice(change.price, change.datePriceEstimated) : undefined}
          />
          <DetailRow label="Telematics ID" value={item.telematicsId} />
          <DetailRow label="Status" value={item.status} />
        </div>
        {onEdit && (
          <DialogFooter className="mt-0">
            <Button onClick={onEdit}>
              <Pencil />
              Edit {label}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
